import { useState, useCallback, useEffect } from 'react';
import emailSyncConfigService from '../services/emailSyncConfigService';

export const useEmailRules = (
	tenantLevel,
	tenant,
	inclusionEnabled,
	open,
	showNotification
) => {
	const [domainRules, setDomainRules] = useState([]);
	const [senderTypeRules, setSenderTypeRules] = useState([]);
	const [specificEmailRules, setSpecificEmailRules] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	// Fetch email rules
	const fetchEmailRules = useCallback(async () => {
		if (!open) return;

		setIsLoading(true);
		try {
			const rulesResponse = await emailSyncConfigService.getEmailRules(
				tenantLevel,
				tenant.id,
				inclusionEnabled
			);

			const grouped = Object.groupBy(rulesResponse, (r) => r.criteria_type);
			setDomainRules(grouped.domain || []);
			setSenderTypeRules(grouped.sender_type || []);
			setSpecificEmailRules(grouped.specific_email || []);
		} catch (error) {
			console.error('Failed to fetch email rules:', error.message);
			showNotification('Failed to load email rules', 'error');
		} finally {
			setIsLoading(false);
		}
	}, [tenantLevel, tenant.id, inclusionEnabled, open]);

	// Fetch email rules
	const fetchInitialEmailRules = useCallback(async (inclusionEnabledValue) => {
		try {
			const rulesResponse = await emailSyncConfigService.getEmailRules(
				tenantLevel,
				tenant.id,
				inclusionEnabledValue
			);

			return rulesResponse
		} catch (error) {
			console.error('Failed to fetch email rules:', error.message);
			showNotification('Failed to load email rules', 'error');
		}
	}, [tenantLevel, tenant.id, inclusionEnabled, open]);

	// Add email rule
	const addEmailRule = useCallback(
		async (criteriaType, criteriaValue) => {
			const value = criteriaValue.trim();
			if (!value) return { success: false, error: 'Value is required' };

			try {
				const newRule = await emailSyncConfigService.createEmailRule(
					tenantLevel,
					tenant.id,
					inclusionEnabled,
					criteriaType,
					value
				);

				if (criteriaType === 'domain') {
					setDomainRules([...domainRules, newRule]);
				} else if (criteriaType === 'sender_type') {
					setSenderTypeRules([...senderTypeRules, newRule]);
				} else if (criteriaType === 'specific_email') {
					setSpecificEmailRules([...specificEmailRules, newRule]);
				}

				showNotification('Email rule saved successfully', 'success');
				return { success: true, data: newRule };
			} catch (error) {
				console.error('Failed to save email rule due to error:', error.message);
				showNotification('Failed to save email rule', 'error');
				return { success: false, error };
			}
		},
		[tenantLevel, tenant.id, inclusionEnabled, domainRules, senderTypeRules, specificEmailRules]
	);

	// Delete email rule
	const deleteEmailRule = useCallback(
		async (criteriaType, ruleId) => {
			try {
				await emailSyncConfigService.deleteEmailRule(
					tenantLevel,
					tenant.id,
					ruleId
				);

				// Optimistically remove from UI
				if (criteriaType === 'domain') {
					setDomainRules(domainRules.filter((r) => r.id !== ruleId));
				} else if (criteriaType === 'sender_type') {
					setSenderTypeRules(senderTypeRules.filter((r) => r.id !== ruleId));
				} else if (criteriaType === 'specific_email') {
					setSpecificEmailRules(specificEmailRules.filter((r) => r.id !== ruleId));
				}

				showNotification('Email rule removed successfully', 'success');
				return { success: true };
			} catch (error) {
				console.error(
					'Failed to remove email rule due to the error:',
					error.message
				);
				showNotification('Failed to remove email rule', 'error');
				return { success: false, error };
			}
		},
		[
			tenantLevel,
			tenant.id,
			domainRules,
			senderTypeRules,
			specificEmailRules,
		]
	);

	// Fetch rules when dependencies change
	useEffect(() => {
		fetchEmailRules();
	}, [fetchEmailRules]);

	return {
		domainRules,
		senderTypeRules,
		specificEmailRules,
		addEmailRule,
		deleteEmailRule,
		fetchEmailRules,
		fetchInitialEmailRules,
		isLoading,
	};
};
